import React, { useState, useMemo, useDeferredValue, useId } from 'react';
import {
  Box,
  createListCollection,
  Field,
  Input,
  Portal,
  Select,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

// Sentinel item passed to createListCollection when the caller's option list is
// empty. Chakra v3's NativeSelect machine crashes in `syncSelectElement` when
// the collection has zero items (Sentry SMS-REACT-11) — keeping one disabled
// placeholder in the collection keeps the machine happy while the menu body
// still renders the "No option available" affordance.
//
// The same machine also crashes when the controlled `value` references
// an option that isn't in the current collection — Chakra can't reconcile its
// hidden native <select>, so we gate value/defaultValue on membership below.
const EMPTY_COLLECTION_SENTINEL = '__select_input_empty__';
const EMPTY_PLACEHOLDER: Option = {
  value: EMPTY_COLLECTION_SENTINEL,
  label: 'No options available',
};

interface SelectInputProps {
  label?: string;
  name?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  option?: Option[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** @deprecated Portal is always enabled; accepted and ignored for backwards compatibility. */
  useMenuPortal?: boolean;
  e2eTitle?: string;
  'data-e2e'?: string;
  [key: string]: any;
  error?: string;
  onScrollToEnd?: () => void;
}

/**
 * @useFor a single-select dropdown inside a `<Field.Root>` — label,
 *   error state, optional search filter, optional clear button.
 *
 *   Examples in-tree: role pickers, status pickers, school pickers
 *   in edit drawers; filter dropdowns above lists.
 *
 *   For multi-select / tag-style use `<SelectMulti>` (searchable
 *   combobox). For very large option lists with type-ahead use
 *   `<SelectMulti>` as well — its filtering is more performant.
 *   For toggle-style binary choice use `<Toggle>` instead of a
 *   two-option select.
 */
const SelectInput: React.FC<SelectInputProps> = ({
  label,
  name,
  id,
  defaultValue,
  value,
  onChange,
  option = [],
  placeholder = 'Select option',
  searchable = true,
  searchPlaceholder = 'Search...',
  useMenuPortal: _useMenuPortal,
  e2eTitle,
  'data-e2e': dataE2EProp,
  error,
  onScrollToEnd,
  ...rest
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const displayPlaceholder =
    placeholder === 'Select option'
      ? t('select_option', 'Select option')
      : placeholder;

  const localizedEmptyPlaceholder = useMemo<Option>(
    () => ({
      value: EMPTY_COLLECTION_SENTINEL,
      label: t('no_options_available', 'No options available'),
    }),
    [t],
  );
  // Coerce `option` to an array. Callers occasionally pass `undefined` / `null`
  // / a non-array (e.g. a hook result while still loading) and .filter/.length
  // would throw.
  const safeOption = useMemo<Option[]>(
    () => (Array.isArray(option) ? option : []),
    [option],
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return safeOption;
    const query = searchQuery.toLowerCase();
    return safeOption.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [safeOption, searchQuery]);

  const deferredFilteredOptions = useDeferredValue(filteredOptions);

  // Drop items whose `value` isn't a non-empty string before building the
  // collection. A caller .map() that yields `{ label, value: undefined }`
  // would otherwise produce options Chakra can't reconcile against a controlled
  // `value` prop.
  const sanitizedOptions = useMemo(
    () =>
      deferredFilteredOptions.filter(
        (o) => typeof o.value === 'string' && o.value.length > 0,
      ),
    [deferredFilteredOptions],
  );

  const collection = useMemo(
    () =>
      createListCollection({
        items:
          sanitizedOptions.length > 0
            ? sanitizedOptions
            : [localizedEmptyPlaceholder],
        isItemDisabled: (item) =>
          Boolean(item.disabled) || item.value === EMPTY_COLLECTION_SENTINEL,
      }),
    [sanitizedOptions, localizedEmptyPlaceholder],
  );

  // Only hand Chakra a `value` / `defaultValue` whose option exists in the
  // current collection. Chakra v3's NativeSelect machine crashes in
  // `syncSelectElement` when it's asked to select an option that isn't there —
  // this happens transiently when `useDeferredValue` lags the collection by
  // one render behind the controlled `value`.
  const chakraValue = useMemo<string[] | undefined>(() => {
    if (value === undefined) return undefined;
    if (!value) return [];
    return sanitizedOptions.some((o) => o.value === value) ? [value] : [];
  }, [value, sanitizedOptions]);

  const chakraDefaultValue = useMemo<string[] | undefined>(() => {
    if (defaultValue === undefined) return undefined;
    if (!defaultValue) return [];
    return sanitizedOptions.some((o) => o.value === defaultValue)
      ? [defaultValue]
      : [];
  }, [defaultValue, sanitizedOptions]);

  const showSearch = searchable && safeOption.length > 5;

  // When Select.Root is rendered inside Field.Root,
  // ark-ui's useSelect sets `ids.hiddenSelect = field.ids.control`. That ID
  // actually points at the <Select.Trigger> <button>, not a native <select>.
  // The zag-js machine's `syncSelectElement` then runs
  // `for (const option of selectEl.options)` on the button — and
  // `button.options` is undefined, producing "r.options is not iterable" the
  // moment `value` transitions from `[]` to a non-empty value. Override the
  // hidden-select id with a stable, unique sentinel so `getById()` returns
  // null and the machine's `if (!selectEl) return` guard kicks in. We don't
  // render <Select.HiddenSelect> either — this component is not
  // form-integrated (no caller relies on native <form> submission).
  const reactId = useId();
  // Bind the focusable Select.Trigger to the consumer-provided id (if any) so
  // an external `<Field.Label htmlFor={id}>` actually focuses the trigger
  // button. Without this, zag-js generates an opaque trigger id like
  // `select::<random>::trigger` that no label can target — silently breaking
  // the WCAG name-association the consumer is trying to set up.
  const externalTriggerId = id ?? name;
  const selectIds = useMemo(
    () => ({
      hiddenSelect: `__aegix-select-input-hidden-${reactId}__`,
      ...(externalTriggerId ? { trigger: externalTriggerId } : {}),
    }),
    [reactId, externalTriggerId],
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
    if (isNearBottom && onScrollToEnd) {
      onScrollToEnd();
    }
  };

  return (
    <Field.Root width="100%" {...rest}>
      {label && (
        <Field.Label color="text.primary" htmlFor={name}>
          {label}
        </Field.Label>
      )}

      <Select.Root
        id={name}
        name={name}
        collection={collection}
        ids={selectIds}
        {...(chakraDefaultValue !== undefined && {
          defaultValue: chakraDefaultValue,
        })}
        {...(chakraValue !== undefined && { value: chakraValue })}
        width="100%"
        onValueChange={(val) => {
          const selected = val.value[0];
          if (
            selected === undefined ||
            selected === EMPTY_COLLECTION_SENTINEL
          ) {
            return;
          }
          onChange?.(selected);
        }}
        onOpenChange={(details) => {
          if (!details.open) {
            setSearchQuery('');
          }
        }}
      >
        <Select.Control>
          <Select.Trigger data-e2e={e2eTitle || dataE2EProp}>
            <Select.ValueText placeholder={displayPlaceholder} />
            <Select.Indicator />
          </Select.Trigger>
        </Select.Control>
        {error && (
          <Text fontSize="xs" color="text.danger">
            {t(error)}
          </Text>
        )}

        <Portal>
          <Select.Positioner>
            <Select.Content>
              {showSearch && (
                <Box
                  p={2}
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  position="sticky"
                  top={0}
                  bg="surface.primary"
                  zIndex={1}
                >
                  <Input
                    // Defaults are load-bearing here, not decoration: this component is
                    // shared across AIM/SMS/One and their catalogs disagree —
                    // SMS defines `search` as an OBJECT (search.globalSearch,
                    // search.placeholder, …), and i18next echoes the raw key when a
                    // lookup lands on an object. Without the fallback a screen
                    // reader announces the literal string "search".
                    aria-label={`${t('search', 'Search')} ${label || t('options', 'options')}`}
                    placeholder={t('search_placeholder', 'Search...')}
                    size="sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    bg="surface.secondary"
                    borderColor="border.secondary"
                    color="text.primary"
                    _placeholder={{ color: 'text.tertiary' }}
                    autoFocus
                    data-e2e="search-input"
                  />
                </Box>
              )}
              <Box
                overflowY="auto"
                maxHeight={showSearch ? '240px' : '300px'}
                onScroll={handleScroll}
              >
                {sanitizedOptions.length > 0 ? (
                  sanitizedOptions.map((opt) => (
                    <Select.Item
                      key={opt.value}
                      item={opt}
                      data-e2e={`option-${opt.value}`}
                    >
                      {opt.label}
                    </Select.Item>
                  ))
                ) : (
                  <Select.Item
                    item={localizedEmptyPlaceholder}
                    data-e2e="no-options"
                  >
                    {searchQuery
                      ? t('no_matching_options', 'No matching options')
                      : t('no_options_available', 'No options available')}
                  </Select.Item>
                )}
              </Box>
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Field.Root>
  );
};

export default SelectInput;
